import { useEffect, useRef, useState } from 'react'
import { Minus, Plus } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import type { Lang } from '../../lib/i18n'
import { searchProducts, type LookupProduct } from '../../lib/intake-api'
import { amendOrderItems, type AmendItemInput, type OrderDetail } from '../../lib/orders-api'
import { useRouteDraft } from '../../lib/route-draft'
import { formatMoney } from './format'
import { ot } from './orders-i18n'

interface Props {
  trigger: React.ReactNode
  order: OrderDetail
  lang: Lang
  isOnline: boolean
  onSaved: () => void
}

// One editable line. productId/variantId come from the snapshots the server wrote; the amend
// route matches an existing line by its variant, so a kept line keeps its variant id.
interface Line {
  key: string
  productId: string
  variantId?: string
  nameEn?: string
  nameAr?: string
  unitPrice: string
  quantity: number
}

function linesOf(order: OrderDetail): Line[] {
  return order.items.map((item) => {
    const productId = typeof item.productSnapshot.id === 'string' ? item.productSnapshot.id : ''
    const variantId = typeof item.variantSnapshot?.id === 'string' ? item.variantSnapshot.id : undefined
    return {
      key: item.id,
      productId,
      variantId,
      nameEn: item.productSnapshot.nameEn,
      nameAr: item.productSnapshot.nameAr,
      unitPrice: item.unitPrice,
      quantity: item.quantity,
    }
  })
}

function bodyOf(lines: Line[]): AmendItemInput[] {
  return lines.map((l) => (l.variantId ? { productId: l.productId, variantId: l.variantId, quantity: l.quantity } : { productId: l.productId, quantity: l.quantity }))
}

// R492: what the person typed, kept per route for the session (lib/route-draft.ts). `base` is
// the order's own item list the draft was made against: a draft is restored only over the same
// list, never over an order someone else has amended since.
interface AmendDraft {
  base: string
  lines: Line[]
  reason: string
}

export function EditItemsDialog({ trigger, order, lang, isOnline, onSaved }: Props) {
  const [open, setOpen] = useState(false)
  const base = JSON.stringify(bodyOf(linesOf(order)))
  const amendDraft = useRouteDraft<AmendDraft>('amend')
  const restorable = amendDraft.draft && amendDraft.draft.base === base ? amendDraft.draft : null
  const [lines, setLines] = useState<Line[]>(() => restorable?.lines ?? linesOf(order))
  const [reason, setReason] = useState(() => restorable?.reason ?? '')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<LookupProduct[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(false)
  const [pending, setPending] = useState(false)
  const pendingRef = useRef(false)
  const [error, setError] = useState<string | null>(null)

  // Opening the dialog starts from the draft when there is one; closing it any way but Cancel
  // keeps the draft for the next open.
  const reset = (from: AmendDraft | null = null) => {
    setLines(from?.lines ?? linesOf(order))
    setReason(from?.reason ?? '')
    setQuery('')
    setResults(null)
    setSearching(false)
    setSearchError(false)
    setError(null)
  }

  const changed = JSON.stringify(bodyOf(lines)) !== base

  // While the dialog is open every edit is the draft; back to the order as it is, no draft.
  const { save: saveDraft, clear: clearDraft } = amendDraft
  useEffect(() => {
    if (!open) return
    if (changed || reason.trim()) saveDraft({ base, lines, reason })
    else clearDraft()
  }, [open, changed, base, lines, reason, saveDraft, clearDraft])
  const lastLine = lines.length === 1
  const canSave = isOnline && changed && !pending

  const setQty = (key: string, quantity: number) => {
    if (quantity < 1) return
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, quantity } : l)))
  }

  const remove = (key: string) => {
    // R459 (c): removing the last line refuses here; the server refuses an empty list too.
    if (lines.length <= 1) return
    setLines((ls) => ls.filter((l) => l.key !== key))
  }

  const add = (product: LookupProduct) => {
    setLines((ls) => {
      // The same product already on the order gains one; a second line for it would collide on
      // the server, which keys lines by variant.
      const existing = ls.find((l) => l.productId === product.id)
      if (existing) return ls.map((l) => (l === existing ? { ...l, quantity: l.quantity + 1 } : l))
      return [
        ...ls,
        { key: `new-${product.id}`, productId: product.id, nameEn: product.nameEn, nameAr: product.nameAr, unitPrice: product.price, quantity: 1 },
      ]
    })
    setResults(null)
    setQuery('')
  }

  const search = async () => {
    if (!query.trim() || searching) return
    setSearching(true)
    setSearchError(false)
    try {
      const found = await searchProducts(query.trim())
      setResults(found.slice(0, 20))
    } catch {
      setResults(null)
      setSearchError(true)
    } finally {
      setSearching(false)
    }
  }

  const save = async () => {
    // A double tap reaches here twice before the re-render disables the button: the ref holds.
    if (!canSave || pendingRef.current) return
    pendingRef.current = true
    setPending(true)
    setError(null)
    try {
      const res = await amendOrderItems(order.id, {
        items: bodyOf(lines),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        expectedStatus: order.status,
      })
      if (res.outcome === 'sent') {
        clearDraft()
        setOpen(false)
        reset()
        onSaved()
        return
      }
      if (res.code === 'AMEND_CUTOFF') setError(ot(lang, 'editItemsCutoff'))
      else if (res.code === 'STATUS_MOVED') setError(ot(lang, 'editItemsMoved'))
      else if (res.code === 'AMEND_EMPTY') setError(ot(lang, 'editItemsLastLine'))
      else setError(ot(lang, 'editItemsFailed')(res.message))
    } catch (err) {
      setError(ot(lang, 'editItemsFailed')(err instanceof Error ? err.message : String(err)))
    } finally {
      pendingRef.current = false
      setPending(false)
    }
  }

  const nameOf = (l: { nameEn?: string; nameAr?: string; productId: string }) =>
    (lang === 'ar' ? l.nameAr : l.nameEn) ?? l.nameEn ?? l.productId

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          reset(restorable)
          setOpen(true)
        } else if (!pendingRef.current) {
          setOpen(false)
          reset()
        }
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent dir={lang === 'ar' ? 'rtl' : 'ltr'} className="max-h-[90vh] grid-cols-[minmax(0,1fr)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{ot(lang, 'editItemsTitle')}</DialogTitle>
          <DialogDescription>{ot(lang, 'editItemsBody')}</DialogDescription>
        </DialogHeader>

        <ul className="divide-y rounded-md border" data-testid="edit-items-lines">
          {lines.map((l) => (
            <li key={l.key} className="flex flex-wrap items-center gap-2 px-3 py-2" data-testid="edit-items-line">
              <div className="min-w-0 flex-1 basis-full sm:basis-0">
                <p className="line-clamp-2 text-sm font-medium text-foreground">{nameOf(l)}</p>
                <p className="text-xs text-muted-foreground" dir="ltr">{formatMoney(l.unitPrice, order.currency)}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-11 w-11"
                  aria-label={ot(lang, 'editItemsDecrease')}
                  title={l.quantity <= 1 ? ot(lang, 'editItemsMinOne') : undefined}
                  disabled={pending || l.quantity <= 1}
                  onClick={() => setQty(l.key, l.quantity - 1)}
                >
                  <Minus className="h-4 w-4" aria-hidden />
                </Button>
                <span className="min-w-[2.5rem] text-center text-base font-semibold tabular-nums" data-testid="edit-items-qty" dir="ltr">
                  {l.quantity}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-11 w-11"
                  aria-label={ot(lang, 'editItemsIncrease')}
                  disabled={pending}
                  onClick={() => setQty(l.key, l.quantity + 1)}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="min-h-[44px] px-3 text-destructive hover:bg-destructive/10"
                  disabled={pending || lastLine}
                  aria-describedby={lastLine ? 'edit-items-last-line' : undefined}
                  onClick={() => remove(l.key)}
                >
                  {ot(lang, 'editItemsRemove')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
        {lastLine && (
          <p id="edit-items-last-line" className="text-sm text-muted-foreground">
            {ot(lang, 'editItemsLastLine')}
          </p>
        )}

        <div className="space-y-2">
          <label htmlFor="edit-items-search" className="block text-sm font-medium text-foreground">
            {ot(lang, 'editItemsAddLabel')}
          </label>
          <div className="flex gap-2">
            <Input
              id="edit-items-search"
              dir="ltr"
              value={query}
              disabled={pending}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void search()
                }
              }}
            />
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px]"
              disabled={pending || searching || !query.trim() || !isOnline}
              onClick={() => void search()}
            >
              {searching ? ot(lang, 'editItemsSearching') : ot(lang, 'editItemsSearch')}
            </Button>
          </div>
          {searchError && <p role="alert" className="text-sm text-destructive">{ot(lang, 'editItemsSearchFailed')}</p>}
          {results && results.length === 0 && <p className="text-sm text-muted-foreground">{ot(lang, 'editItemsNoProducts')}</p>}
          {results && results.length > 0 && (
            <div className="max-h-48 space-y-1 overflow-y-auto" data-testid="edit-items-results">
              {results.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-start text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                  disabled={pending}
                  onClick={() => add(p)}
                >
                  <span className="min-w-0 truncate">{nameOf({ nameEn: p.nameEn ?? p.slug, nameAr: p.nameAr, productId: p.id })}</span>
                  <span className="shrink-0 text-muted-foreground" dir="ltr">{formatMoney(p.price, order.currency)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="edit-items-reason" className="mb-1 block text-sm font-medium text-foreground">
            {ot(lang, 'editItemsReasonLabel')}
          </label>
          <Input id="edit-items-reason" value={reason} disabled={pending} onChange={(e) => setReason(e.target.value)} />
        </div>

        {!isOnline && <p role="status" className="text-sm text-destructive">{ot(lang, 'editItemsOffline')}</p>}
        {isOnline && !changed && !error && <p className="text-sm text-muted-foreground">{ot(lang, 'editItemsNoChange')}</p>}
        {error && <p role="alert" className="text-sm text-destructive" data-testid="edit-items-error">{error}</p>}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            size="lg"
            className="min-h-[48px]"
            disabled={pending}
            onClick={() => {
              // Cancel is the one close that throws the typing away.
              clearDraft()
              setOpen(false)
              reset()
            }}
          >
            {ot(lang, 'editItemsCancel')}
          </Button>
          <Button size="lg" className="min-h-[48px]" disabled={!canSave} onClick={() => void save()}>
            {pending ? ot(lang, 'editItemsSaving') : ot(lang, 'editItemsSave')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
